import { useState } from "react";
import { useEditMode } from "@/contexts/EditModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, Edit2, Save, X, Plus, Trash2 } from "lucide-react";

interface Plan {
  name: string;
  price: number;
  original_price?: number;
  setup_fee?: string;
  features: string[] | { [key: string]: any };
  target_audience?: 'new_agents' | 'existing_agents' | 'general';
}

interface EditablePlansProps {
  plans: Plan[];
  onSave: (plans: Plan[]) => Promise<void>;
}

// Helper to convert features object to array for editing
const featuresToArray = (features: string[] | { [key: string]: any } | undefined): string[] => {
  if (!features) return [''];
  if (Array.isArray(features)) return features.length > 0 ? features : [''];
  // Convert object to "key: value" strings
  return Object.entries(features).map(([key, value]) => 
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? `${key}: ${value}` 
      : key
  );
};

// Helper to convert array back to object for saving
const arrayToFeatures = (features: string[]): { [key: string]: any } => {
  const result: { [key: string]: any } = {};
  features.forEach(feature => {
    const colonIndex = feature.indexOf(':');
    if (colonIndex > -1) {
      const key = feature.substring(0, colonIndex).trim();
      const value = feature.substring(colonIndex + 1).trim();
      result[key] = value;
    } else {
      result[feature] = true;
    }
  });
  return result;
};

export default function EditablePlans({ plans, onSave }: EditablePlansProps) {
  const { isEditMode } = useEditMode();
  const { isEditor } = useAuth();
  
  // Convert plans to array format for editing
  const [isEditing, setIsEditing] = useState(true);
  const [editedPlans, setEditedPlans] = useState<Array<{ name: string; price: number; original_price?: number; setup_fee?: string; features: string[]; target_audience?: 'new_agents' | 'existing_agents' | 'general' }>>(
    plans.map(p => ({
      name: p.name,
      price: p.price,
      original_price: p.original_price,
      setup_fee: p.setup_fee,
      features: featuresToArray(p.features),
      target_audience: p.target_audience || 'general'
    }))
  );
  const [isSaving, setIsSaving] = useState(false);

  if (!isEditor || !isEditMode) {
    return null;
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const validPlans = editedPlans.filter(p => p.name.trim() && p.price > 0);
      // Convert features back to object format for saving
      const plansToSave = validPlans.map(p => ({
        name: p.name,
        price: p.price,
        original_price: p.original_price,
        setup_fee: p.setup_fee,
        features: arrayToFeatures(p.features.filter(f => f.trim())),
        target_audience: p.target_audience || 'general'
      }));
      await onSave(plansToSave as any);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save plans:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedPlans(plans.map(p => ({
      name: p.name,
      price: p.price,
      original_price: p.original_price,
      setup_fee: p.setup_fee,
      features: featuresToArray(p.features),
      target_audience: p.target_audience || 'general'
    })));
    setIsEditing(false);
  };

  const handleAddPlan = () => {
    setEditedPlans([...editedPlans, { name: "", price: 0, original_price: 0, setup_fee: "", features: [""], target_audience: 'general' }]);
  };

  const handleRemovePlan = (index: number) => {
    setEditedPlans(editedPlans.filter((_, i) => i !== index));
  };

  const handleUpdatePlanName = (index: number, name: string) => {
    const updated = [...editedPlans];
    updated[index].name = name;
    setEditedPlans(updated);
  };

  const handleUpdatePlanPrice = (index: number, price: string) => {
    const updated = [...editedPlans];
    updated[index].price = parseFloat(price) || 0;
    setEditedPlans(updated);
  };

  const handleUpdatePlanOriginalPrice = (index: number, originalPrice: string) => {
    const updated = [...editedPlans];
    updated[index].original_price = parseFloat(originalPrice) || undefined;
    setEditedPlans(updated);
  };

  const handleUpdatePlanSetupFee = (index: number, setupFee: string) => {
    const updated = [...editedPlans];
    updated[index].setup_fee = setupFee;
    setEditedPlans(updated);
  };

  const handleAddFeature = (planIndex: number) => {
    const updated = [...editedPlans];
    updated[planIndex].features.push("");
    setEditedPlans(updated);
  };

  const handleRemoveFeature = (planIndex: number, featureIndex: number) => {
    const updated = [...editedPlans];
    updated[planIndex].features = updated[planIndex].features.filter((_, i) => i !== featureIndex);
    setEditedPlans(updated);
  };

  const handleUpdateFeature = (planIndex: number, featureIndex: number, value: string) => {
    const updated = [...editedPlans];
    updated[planIndex].features[featureIndex] = value;
    setEditedPlans(updated);
  };

  const handleUpdateTargetAudience = (index: number, audience: 'new_agents' | 'existing_agents' | 'general') => {
    const updated = [...editedPlans];
    updated[index].target_audience = audience;
    setEditedPlans(updated);
  };

  if (!isEditing) {
    return (
      <div className="relative group">
        <div className="absolute -top-2 -right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size="sm"
            onClick={() => setIsEditing(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white shadow-lg"
          >
            <Edit2 className="h-3 w-3 mr-1" />
            Edit Plans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 border-2 border-dashed border-orange-400 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-orange-600">Editing Plans</h4>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleAddPlan}>
            <Plus className="h-3 w-3 mr-1" />
            Add Plan
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            <Save className="h-3 w-3 mr-1" />
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel}>
            <X className="h-3 w-3 mr-1" />
            Cancel
          </Button>
        </div>
      </div>

      {editedPlans.map((plan, planIndex) => (
        <Card key={planIndex} className="p-4 border-2 border-orange-200">
          <div className="space-y-3">
            <div className="flex gap-2 items-start">
              <div className="flex-1 space-y-2">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Plan Name</label>
                  <Input
                    value={plan.name}
                    onChange={(e) => handleUpdatePlanName(planIndex, e.target.value)}
                    placeholder="e.g., Single User, Team Plan"
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-muted-foreground">Price ($)</label>
                    <Input
                      type="number"
                      value={plan.price}
                      onChange={(e) => handleUpdatePlanPrice(planIndex, e.target.value)}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-muted-foreground">Original Price ($)</label>
                    <Input
                      type="number"
                      value={plan.original_price || ''}
                      onChange={(e) => handleUpdatePlanOriginalPrice(planIndex, e.target.value)}
                      placeholder="Optional"
                      className="mt-1"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-muted-foreground">Setup Fee</label>
                    <Input
                      value={plan.setup_fee || ''}
                      onChange={(e) => handleUpdatePlanSetupFee(planIndex, e.target.value)}
                      placeholder="e.g., $500, Included"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Target Audience</label>
                  <select
                    value={plan.target_audience || 'general'}
                    onChange={(e) => handleUpdateTargetAudience(planIndex, e.target.value as 'new_agents' | 'existing_agents' | 'general')}
                    className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="general">General Audience</option>
                    <option value="new_agents">New Agents</option>
                    <option value="existing_agents">Existing Agents</option>
                  </select>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleRemovePlan(planIndex)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-muted-foreground">Features</label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAddFeature(planIndex)}
                  className="h-6 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Feature
                </Button>
              </div>
              <div className="space-y-2">
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex gap-2 items-center">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <Input
                      value={feature}
                      onChange={(e) => handleUpdateFeature(planIndex, featureIndex, e.target.value)}
                      placeholder="Feature description"
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveFeature(planIndex, featureIndex)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 px-2"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
